import { atom } from 'jotai';

export type AddUserDialogStep = 'userInfo' | 'roleAssignment';

export interface AddUserFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
}

export interface RoleAssignment {
  roleId: string;
  facilityId: string;
}

const getInitialFormData = (): AddUserFormData => ({
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
});

export const $addUserDialogOpen = atom(false);
export const $addUserDialogStep = atom<AddUserDialogStep>('userInfo');
export const $addUserFormData = atom<AddUserFormData>(getInitialFormData());
export const $selectedRoleAssignments = atom<RoleAssignment[]>([]);

export const $openAddUserDialog = atom(null, (_get, set) => {
  set($addUserFormData, getInitialFormData());
  set($addUserDialogStep, 'userInfo');
  set($selectedRoleAssignments, []);
  set($addUserDialogOpen, true);
});

export const $closeAddUserDialog = atom(null, (_get, set) => {
  set($addUserDialogOpen, false);
  set($addUserFormData, getInitialFormData());
  set($addUserDialogStep, 'userInfo');
  set($selectedRoleAssignments, []);
});

export const $updateAddUserForm = atom(
  null,
  (get, set, update: Partial<AddUserFormData>) => {
    set($addUserFormData, { ...get($addUserFormData), ...update });
  },
);

export const $addRoleAssignment = atom(
  null,
  (get, set, assignment: RoleAssignment) => {
    const current = get($selectedRoleAssignments);
    const exists = current.some(
      (a) =>
        a.roleId === assignment.roleId &&
        a.facilityId === assignment.facilityId,
    );
    if (!exists) {
      set($selectedRoleAssignments, [...current, assignment]);
    }
  },
);

export const $removeRoleAssignment = atom(
  null,
  (get, set, assignment: RoleAssignment) => {
    const current = get($selectedRoleAssignments);
    set(
      $selectedRoleAssignments,
      current.filter(
        (a) =>
          a.roleId !== assignment.roleId ||
          a.facilityId !== assignment.facilityId,
      ),
    );
  },
);

export const $clearAssignmentsByRole = atom(
  null,
  (get, set, roleId: string) => {
    const current = get($selectedRoleAssignments);
    set(
      $selectedRoleAssignments,
      current.filter((a) => a.roleId !== roleId),
    );
  },
);

export const $goToRoleStep = atom(null, (_get, set) => {
  set($addUserDialogStep, 'roleAssignment');
});

export const $goToUserInfoStep = atom(null, (_get, set) => {
  set($addUserDialogStep, 'userInfo');
});

export const $canProceedToRoles = atom((get) => {
  const form = get($addUserFormData);
  return (
    form.firstName.trim() !== '' &&
    form.lastName.trim() !== '' &&
    form.email.trim() !== '' &&
    form.phoneNumber.trim() !== ''
  );
});

export const $canSendInvite = atom((get) => {
  const formValid = get($canProceedToRoles);
  const assignments = get($selectedRoleAssignments);
  return formValid && assignments.length > 0;
});
